import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, RouteComponentProps } from 'react-router';
import { renderRoutes } from 'react-router-config';
import { Url, USER_PROFILE_POSTS_SUB_PATHS } from '@wix/da-url';
import HiddenIcon from '@wix/da-shared-react/pkg/Icons/Hidden';
import Separator from '@wix/da-shared-react/pkg/Separator';
import SubSubNavBar, { SubSubNavBarItem } from '../../SubSubNavBar';
import { PostsSortOrder } from '../../../../types/posts';

import PostsAll from './PostsAll';
import Journals from './Journals';
import Statuses from './Statuses';
import Polls from './Polls';
import Drafts from './Drafts';
import SectionViewTypeSwitch from '../../SectionViewSwitch';
import { SectionViewType } from '../../../../types/sections';

import { Breakpoint } from '@wix/da-shared-react/pkg/Separator/Separator';
import s from './PostsSection.scss';

export interface Props {
  isOwner: boolean;
  ownerUsername: string;
  sortOrder?: PostsSortOrder;

  allTotal: number;
  journalsTotal: number;
  pollsTotal: number;
  statusesTotal: number;
  draftsTotal: number;
}

export interface RouteParams {
  sortOrder?: PostsSortOrder;
}

export interface State {
  viewType: SectionViewType;
}

const PostsSection: React.FC<Props & RouteComponentProps<RouteParams>> = ({
  ownerUsername,
  sortOrder,
  isOwner,
  allTotal,
  draftsTotal,
  journalsTotal,
  pollsTotal,
  statusesTotal,
  history,
}) => {
  const { t } = useTranslation();
  const [viewType, setViewType] = useState(SectionViewType.LIST);

  const routes = useMemo(() => {
    const username = ownerUsername.toLowerCase();
    const routeList: SubSubNavBarItem[] = [
      {
        label: t('common.all'),
        count: allTotal,
        path: Url.userPostsPath({
          username,
          subPath: USER_PROFILE_POSTS_SUB_PATHS.ALL,
        }),
        component: PostsAll,
        exact: true,
        bi_item_label: 'all',
      },
      {
        label: t('common.journals_plural'),
        count: journalsTotal,
        path: Url.userPostsPath({
          username,
          subPath: USER_PROFILE_POSTS_SUB_PATHS.JOURNALS,
        }),
        component: Journals,
        bi_item_label: USER_PROFILE_POSTS_SUB_PATHS.JOURNALS,
      },
      {
        label: t('common.statuses_plural'),
        count: statusesTotal,
        path: Url.userPostsPath({
          username,
          subPath: USER_PROFILE_POSTS_SUB_PATHS.STATUSES,
        }),
        component: Statuses,
        bi_item_label: USER_PROFILE_POSTS_SUB_PATHS.STATUSES,
      },
      {
        label: t('common.polls_plural'),
        count: pollsTotal,
        path: Url.userPostsPath({
          username,
          subPath: USER_PROFILE_POSTS_SUB_PATHS.POLLS,
        }),
        component: Polls,
        bi_item_label: USER_PROFILE_POSTS_SUB_PATHS.POLLS,
      },
    ];
    if (isOwner) {
      routeList.push({
        label: t('common.drafts_plural'),
        labelIconClass: HiddenIcon,
        tooltipText: t('navigation.subnavigation.draftsVisibility'),
        count: draftsTotal,
        path: Url.userPostsPath({
          username,
          subPath: USER_PROFILE_POSTS_SUB_PATHS.DRAFTS,
        }),
        component: Drafts,
        bi_item_label: USER_PROFILE_POSTS_SUB_PATHS.DRAFTS,
      });
    }
    return routeList;
  }, [
    allTotal,
    draftsTotal,
    isOwner,
    journalsTotal,
    ownerUsername,
    pollsTotal,
    statusesTotal,
    t,
  ]);

  const onSortOrderChange = useCallback(
    (subPath: USER_PROFILE_POSTS_SUB_PATHS, sort: PostsSortOrder) => {
      const postsPath = Url.userPostsPath({
        username: ownerUsername,
        subPath,
        sortOrder: sort,
      });
      history.push(postsPath);
    },
    [history, ownerUsername]
  );

  return (
    <div className={s['root']}>
      <SubSubNavBar
        className={s['section-nav']}
        rootClassName={s['subnav-root']}
        iconClassName={s['subnav-icon']}
        items={routes}
        extras={
          <Route
            key="nav-user"
            path={Url.userPostsPath({
              username: ownerUsername,
              subPath: USER_PROFILE_POSTS_SUB_PATHS.JOURNALS,
            })}
            render={() => (
              <SectionViewTypeSwitch
                className={s['section-view-switch']}
                value={viewType}
                onChange={setViewType}
              />
            )}
          />
        }
      />
      <Separator className={s['separator']} forBreakpoint={Breakpoint.Mobile} />
      {renderRoutes(routes, {
        viewType,
        sortOrder,
        onSortOrderChange: onSortOrderChange,
      })}
    </div>
  );
};
PostsSection.displayName = 'PostsSection';

export default PostsSection;
